import {
  Button,
  closeDialog,
  Dropdown,
  DropdownItem,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Heading, Hide, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {isEmpty, isNil, isNotNil, join, map, pipe, reject, uniq} from 'ramda';
import {isArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {LabeledKey} from '@dms/api/shared';
import {
  useDeleteServiceOrderItemsMutation,
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrderJobsQuery,
  useGetServiceCaseOrdersQuery,
  usePostServiceOrderCalculatePriceBulkMutation,
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartRequestMutation,
  usePostServiceItemsMaterialPartReturnMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  handleApiError,
  queryParams,
  catchUnhandledDataGridActions,
  DmsDataGrid,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import type {
  ActionCallback,
  EagGridMaximization,
  GridDataChange,
  QueryFilterObject,
} from 'features/eag-grid';

import {BulkAssignMechanicForm} from '../../../../../components/BulkAssignMechanicForm';
import {BulkDiscountForm} from '../../../../../components/BulkDiscountForm';
import {BulkVatChangeForm} from '../../../../../components/BulkVatChangeForm';
import {BulkWorkTypeChangeForm} from '../../../../../components/BulkWorkTypeChangeForm';
import {GoodwillForm} from '../../../../../components/Goodwill/GoodwillForm';
import {IssueMaterialItems} from '../../../../../components/IssueMaterialItems';
import {MoveRequestItem} from '../../../../../components/MoveRequestItem/MoveRequestItem';
import {
  RequestEditDialog,
  RequestEditDialogProps,
} from '../../../../../components/RequestEditDialog';
import {WorkSplittingForm} from '../../../../../components/WorkSplitting/WorkSplittingForm';
import {MATERIAL_TYPE} from '../../../../../constants/materialType';
import {ORDER_ITEMS_GRID_CODE} from '../../../../../constants/orderItemsGridCode';
import {useOrderItemsBulkActions} from '../../../../../hooks/useOrderItemsBulkActions';
import {useWorkItemsBulkSelection} from '../../../../../hooks/useWorkItemsBulkSelection';
import {openCreateCoreExchangeDialog} from '../../../../../utils/openCreateCoreExchangeDialog';

type ReceiveJobItemRowData = {
  id: string;
  itemName: {value: string};
  serviceJobId: {value: string};
  itemType: {value: LabeledKey};
  itemPriceType: {value: LabeledKey};
  jobName: {value: string};
  itemGoodwillRatio: {value: {label: string} | Nullish};
  serviceOrderJobItemId: {value: string};
  assignMechanics: {value: {key: string}[]};
};

interface OrderItemsDataGridProps extends TestIdProps {
  maximization?: EagGridMaximization;
  serviceCaseId: string;
  orderId: string;
  isReadOnly?: boolean;
}

const PREDEFINED_COLUMNS = [
  'itemName',
  'itemType',
  'itemPriceType',
  'itemGoodwillRatio',
  'serviceOrderJobItemId',
  'serviceJobId',
  'jobName',
  'assignMechanics',
] as const;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function OrderItemsDataGrid(props: OrderItemsDataGridProps) {
  const [canCreateCoreExchange] = usePermissions({permissionKeys: ['coreExchangeCreate']});
  const [, setAddLabourJobId] = useQueryState(queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID);
  const [, setAddMaterialRequestJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_MATERIAL_REQUEST_JOB_ID
  );
  const [, setServiceOrderReturnId] = useQueryState(queryParams.SERVICE_ORDER_RETURN_ID);
  const [, setServiceOrderReturnAuthorizationProfileId] = useQueryState(
    queryParams.SERVICE_ORDER_RETURN_AUTHORIZATION_PROFILE_ID
  );

  const [editDialogProps, setEditDialogProps] = useState<RequestEditDialogProps | null>(null);
  const [gridData, setGridData] = useState<GridDataChange | null>(null);

  const {data: uncategorizedJob} = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      withUncategorizedWithoutItems: true,
    },
    {
      selectFromResult: ({data}) => ({data: data?.find((job) => job?.isUncategorized)}),
    }
  );

  const serviceCaseQuery = useGetMetadaServiceCaseQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const serviceCaseOrdersQuery = useGetServiceCaseOrdersQuery({serviceCaseId: props.serviceCaseId});

  const [deleteServiceOrderItems] = useDeleteServiceOrderItemsMutation();
  const [postServiceOrderCalculatePriceBulk] = usePostServiceOrderCalculatePriceBulkMutation();
  const [postServiceItemsMaterialPartRequest] = usePostServiceItemsMaterialPartRequestMutation();
  const [postServiceItemsMaterialPartReturn] = usePostServiceItemsMaterialPartReturnMutation();
  const [postServiceItemsMaterialPartRequestCancellation] =
    usePostServiceItemsMaterialPartRequestCancellationMutation();

  const refreshDataWithServiceCaseRefetch = useCallback(
    async (refreshData: VoidFunction) => {
      await Promise.all([serviceCaseQuery.refetch(), serviceCaseOrdersQuery.refetch()]);
      refreshData();
    },
    [serviceCaseOrdersQuery, serviceCaseQuery]
  );

  const itemsSelection = useWorkItemsBulkSelection(ORDER_ITEMS_GRID_CODE, gridData);
  const itemsActions = useOrderItemsBulkActions({
    gridData,
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
    // Mirrors the per-row issue/request/requestCancellation handlers below.
    onActionSuccess: () =>
      refreshDataWithServiceCaseRefetch(() => refreshDmsDatagrid(ORDER_ITEMS_GRID_CODE)),
    'data-testid': suffixTestId('itemsBulkActions', props),
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      serviceCaseId: String(props.serviceCaseId),
      serviceOrderId: String(props.orderId),
    }),
    [props.orderId, props.serviceCaseId]
  );

  const actionCallback: ActionCallback = ({
    queryId,
    rowId,
    actionKey,
    rowData,
    refreshData,
    deselectAll,
  }) => {
    const castedRowData = rowData as ReceiveJobItemRowData | ReceiveJobItemRowData[];

    const itemId = isArray(castedRowData)
      ? castedRowData[0].serviceOrderJobItemId.value
      : castedRowData.serviceOrderJobItemId.value;

    const itemJobId = isArray(castedRowData) ? undefined : castedRowData.serviceJobId.value;

    const items = isArray(castedRowData)
      ? castedRowData.map((row) => ({
          serviceItemId: row.serviceOrderJobItemId.value,
          serviceJobId: row.serviceJobId.value,
        }))
      : [{serviceItemId: itemId, serviceJobId: itemJobId ?? ''}];

    const requestNames = isArray(castedRowData)
      ? getRequestNamesString(castedRowData)
      : castedRowData.jobName.value;

    const hasMaterialItem = (isArray(castedRowData) ? castedRowData : [castedRowData]).some(
      (row) => row.itemType.value.key === MATERIAL_TYPE
    );

    const hasSomeGoodwill =
      isArray(castedRowData) &&
      castedRowData.some(
        (data) =>
          isNotNilOrEmpty(data.itemGoodwillRatio.value) &&
          Number(data.itemGoodwillRatio.value?.label) !== 100
      );

    return match(actionKey)
      .with('getCurrentPrices', () =>
        postServiceOrderCalculatePriceBulk({
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
          body: {serviceItems: items},
        })
          .unwrap()
          .then(() => refreshDataWithServiceCaseRefetch(refreshData))
          .then(deselectAll)
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServiceOrderItems({
              serviceOrderId: props.orderId,
              serviceCaseId: props.serviceCaseId,
              body: {items},
            })
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('edit', 'redirectDetail', () => {
        if (isArray(castedRowData) || isNil(castedRowData)) {
          return;
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
        };

        setEditDialogProps({
          gridCode: ORDER_ITEMS_GRID_CODE,
          rowId: isArray(rowId) ? rowId[0] : rowId,
          dataQueryId: queryId,
          serviceCaseId: props.serviceCaseId,
          orderId: props.orderId,
          itemData: castedRowData,
          onClose: handleClose,
          'data-testid': props['data-testid'] as string,
        });
      })
      .with('move', () =>
        openDialog(
          <MoveRequestItem
            hasMaterialItem={hasMaterialItem}
            onClose={() => closeDialog('moveRequestItem')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onMoveItems={refreshData}
            data-testid={suffixTestId('moveRequestItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.moveRequestItem'),
            id: 'moveRequestItem',
          }
        )
      )
      .with('goodwill', () =>
        openDialog(
          <GoodwillForm
            onClose={() => closeDialog('goodwill')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            requestName={requestNames}
            items={items}
            onSaved={refreshData}
            isWithMultipleGoodwill={hasSomeGoodwill}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.goodwill'),
            id: 'goodwill',
          }
        )
      )
      .with('workSplitting', () =>
        openDialog(
          <WorkSplittingForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('workSplitting')}
            data-testid={suffixTestId('workSplitting', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.workSplitting'),
            id: 'workSplitting',
          }
        )
      )
      .with('request', () =>
        postServiceItemsMaterialPartRequest({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
        })
          .unwrap()
          .then(() => refreshDataWithServiceCaseRefetch(refreshData))
          .catch(handleApiError)
      )
      .with('issue', () => {
        const defaultMechanicId = isArray(castedRowData)
          ? castedRowData[0]?.assignMechanics?.value?.[0]?.key
          : castedRowData?.assignMechanics?.value?.[0]?.key;

        return openDialog(
          <IssueMaterialItems
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            defaultMechanicId={defaultMechanicId}
            onSubmit={() => refreshDataWithServiceCaseRefetch(refreshData)}
            onClose={() => closeDialog('issueMaterialItem')}
            data-testid={suffixTestId('issueMaterialItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.issueMaterialItem'),
            id: 'issueMaterialItem',
            size: 'small',
          }
        );
      })
      .with('assignMechanic', () =>
        openDialog(
          <BulkAssignMechanicForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('assignMechanic')}
            data-testid={suffixTestId('assignMechanic', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.assignMechanic'),
            id: 'assignMechanic',
            size: 'small',
          }
        )
      )
      .with('discount', () =>
        openDialog(
          <BulkDiscountForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('manualDiscount')}
            data-testid={suffixTestId('manualDiscount', props)}
          />,
          {
            title: i18n.t('entity.orderItem.labels.manualDiscount'),
            id: 'manualDiscount',
            size: 'small',
          }
        )
      )
      .with('workTypeChange', () =>
        openDialog(
          <BulkWorkTypeChangeForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('workTypeChange')}
            data-testid={suffixTestId('workTypeChange', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.workTypeChange'),
            id: 'workTypeChange',
            size: 'small',
          }
        )
      )
      .with('vatChange', () =>
        openDialog(
          <BulkVatChangeForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('vatChange')}
            data-testid={suffixTestId('vatChange', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.vatChange'),
            id: 'vatChange',
            size: 'small',
          }
        )
      )
      .with('return', () => {
        if (isNil(props.orderId)) {
          throw new Error('serviceOrderId is not defined');
        }

        postServiceItemsMaterialPartReturn({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
        })
          .unwrap()
          .then((result) => {
            if (isNotNil(result?.serviceOrderReturnId)) {
              setServiceOrderReturnId(result?.serviceOrderReturnId);
            }
            if (isNotNil(result?.authorizationProfileId)) {
              setServiceOrderReturnAuthorizationProfileId(result?.authorizationProfileId);
            }
          })
          .then(deselectAll)
          .then(() => refreshDataWithServiceCaseRefetch(refreshData))
          .catch(handleApiError);
      })
      .with('requestCancellation', () =>
        postServiceItemsMaterialPartRequestCancellation({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
        })
          .unwrap()
          .then(() => refreshDataWithServiceCaseRefetch(refreshData))
          .catch(handleApiError)
      )
      .with('createCoreExchange', () =>
        openCreateCoreExchangeDialog(
          castedRowData,
          props.serviceCaseId,
          props.orderId,
          canCreateCoreExchange,
          props
        )
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleOpenAddLabour = () => {
    uncategorizedJob?.id && setAddLabourJobId(uncategorizedJob.id);
  };

  const handleOpenAddMaterialRequest = () => {
    uncategorizedJob?.id && setAddMaterialRequestJobId(uncategorizedJob.id);
  };

  return (
    <>
      <Show when={isNotNil(editDialogProps)}>
        <RequestEditDialog {...(editDialogProps as RequestEditDialogProps)} />
      </Show>
      <DmsDataGrid
        maximization={props.maximization}
        sectionHeader={
          <HStack justify="space-between">
            <Heading size={4}>{i18n.t('entity.orderRequest.labels.allItems')}</Heading>
            <Hide when={props.isReadOnly}>
              <HStack align="center" spacing={2}>
                {/* The bulk controls are deliberately NOT permission-gated: their
                applicability is backend row-driven (row.actions), the same signal the
                grid uses for its per-row actions. They render only when the EagGrid
                engine reports data — the legacy grid neither calls onGridDataChange
                nor listens to selectDatagridRows. */}
                <Show when={itemsSelection.isAvailable}>
                  <Dropdown
                    data-testid={suffixTestId('itemsMoreActions', props)}
                    dropdownControl={
                      <Button
                        variant="ghostLink"
                        leftIcon="navigation/more_vert"
                        data-testid={suffixTestId('itemsMoreActionsButton', props)}
                        title={i18n.t('entity.orderRequest.actions.moreActions')}
                      />
                    }
                  >
                    <DropdownItem
                      leftIcon="custom/warehouse"
                      label={`${i18n.t('entity.orderRequest.actions.issueMaterial')} (${itemsActions.issueCount})`}
                      onClick={itemsActions.issueMaterial}
                      isDisabled={itemsActions.issueCount === 0}
                      data-testid={suffixTestId('issueMaterial', props)}
                    />
                    <DropdownItem
                      leftIcon="action/shopping_cart"
                      label={`${i18n.t('entity.orderRequest.actions.requestMaterial')} (${itemsActions.requestCount})`}
                      onClick={itemsActions.requestMaterial}
                      isLoading={itemsActions.isRequestMaterialPending}
                      isDisabled={
                        itemsActions.isRequestMaterialPending || itemsActions.requestCount === 0
                      }
                      closeOnClick={false}
                      data-testid={suffixTestId('requestMaterial', props)}
                    />
                    <DropdownItem
                      leftIcon="navigation/cancel"
                      label={`${i18n.t('entity.orderRequest.actions.requestCancellation')} (${itemsActions.cancellationCount})`}
                      onClick={itemsActions.requestCancellation}
                      isLoading={itemsActions.isRequestCancellationPending}
                      isDisabled={
                        itemsActions.isRequestCancellationPending ||
                        itemsActions.cancellationCount === 0
                      }
                      closeOnClick={false}
                      data-testid={suffixTestId('requestCancellation', props)}
                    />
                  </Dropdown>
                  <Button
                    variant="ghostLink"
                    onClick={itemsSelection.selectLabour}
                    isDisabled={itemsSelection.isLoading || itemsSelection.labourCount === 0}
                    data-testid={suffixTestId('selectLabour', props)}
                    title={`${i18n.t('entity.orderRequest.actions.selectLabour')} (${itemsSelection.labourCount})`}
                  />
                  <Button
                    variant="ghostLink"
                    onClick={itemsSelection.selectMaterial}
                    isDisabled={itemsSelection.isLoading || itemsSelection.materialCount === 0}
                    data-testid={suffixTestId('selectMaterial', props)}
                    title={`${i18n.t('entity.orderRequest.actions.selectMaterial')} (${itemsSelection.materialCount})`}
                  />
                </Show>
                <Button
                  variant="ghostLink"
                  leftIcon="content/add_circle"
                  onClick={handleOpenAddLabour}
                  data-testid={suffixTestId('addWork', props)}
                  title={i18n.t('entity.addWork.actions.addWork')}
                />
                <Button
                  variant="ghostLink"
                  leftIcon="content/add_circle"
                  onClick={handleOpenAddMaterialRequest}
                  data-testid={suffixTestId('addMaterial', props)}
                  title={i18n.t('entity.addMaterial.actions.addMaterial')}
                />
              </HStack>
            </Hide>
          </HStack>
        }
        predefinedColumns={PREDEFINED_COLUMNS}
        autoHeight
        gridCode={ORDER_ITEMS_GRID_CODE}
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        onGridDataChange={setGridData}
        data-testid={suffixTestId('serviceCaseOrderItems', props)}
      />
    </>
  );
}

const getRequestNamesString = pipe(
  map((row: ReceiveJobItemRowData) => row.jobName.value),
  reject(isEmpty),
  uniq,
  join(',')
);
